import ButtonGroupCustomized from '@/components/ButtonGroupCustomized'
import { Card } from '@/components/ui/card'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import DownIcons from '@/icons/DownIcons'
import { getProductsGroupOptions } from '@/modules/ProductGroup/services'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import { Button, Checkbox, Typogaphy } from 'vnetwork-ui'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { RevenueAllocationsParams, RevenueCombined } from '../../../types'
import { SCHEMES } from '@/components/EchartCustomized'

interface RevenueChartProps {
    revenueData: RevenueCombined[]
    costData: Array<{
        cost_amount: number
        cost_month?: string
        revenue_month?: string
    }>
    filters?: RevenueAllocationsParams
    selectedProducts: Set<string>
    setSelectedProducts: React.Dispatch<React.SetStateAction<Set<string>>>
}

type ChartView = 'product' | 'monthly'

const RevenueChart: React.FC<RevenueChartProps> = ({
    revenueData,
    costData,
    selectedProducts,
    setSelectedProducts,
}) => {
    const { t } = useTranslation()
    const [chartView, setChartView] = useState<ChartView>('product')
    const [showFilters, setShowFilters] = useState(false)

    // Fetch all product groups from API
    const { data: productGroupOptions } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.PRODUCT_GROUPS],
        queryFn: getProductsGroupOptions,
    })

    // Get list of all product groups from API
    const allProductGroups = useMemo(() => {
        return productGroupOptions?.map((option) => option.value) || []
    }, [productGroupOptions])

    // Format currency for tooltip
    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('vi-VN', {
            style: 'currency',
            currency: 'VND',
        }).format(value)
    }

    // Handle toggle individual product
    const handleToggleProduct = (product: string) => {
        const newSelected = new Set(selectedProducts)
        if (newSelected.has(product)) {
            newSelected.delete(product)
        } else {
            newSelected.add(product)
        }
        setSelectedProducts(newSelected)
    }

    // Handle select all / deselect all
    const handleToggleAll = () => {
        if (selectedProducts.size === allProductGroups.length) {
            setSelectedProducts(new Set())
        } else {
            setSelectedProducts(new Set(allProductGroups))
        }
    }

    // Data for product group trend chart (line chart showing trend over time)
    const productTrendData = useMemo(() => {
        if (!revenueData.length) return []

        // Filter by selected products
        const filteredRevenue = revenueData.filter((item) =>
            selectedProducts.has(item.product_group_name)
        )

        // Group by month, then by product
        const monthlyByProduct = filteredRevenue.reduce(
            (acc, item) => {
                const month = item.revenue_month
                const product = item.product_group_name

                if (!acc[month]) {
                    acc[month] = { month }
                }

                if (!acc[month][product]) {
                    acc[month][product] = 0
                }

                acc[month][product] +=
                    item.revenue_amount || item.allocated_amount || 0
                return acc
            },
            {} as Record<string, any>
        )

        // Convert to array and sort by month
        return Object.values(monthlyByProduct).sort((a: any, b: any) =>
            a.month.localeCompare(b.month)
        )
    }, [revenueData, selectedProducts])

    // Get list of selected product groups for rendering
    const visibleProductGroups = useMemo(() => {
        return allProductGroups.filter((product) =>
            selectedProducts.has(product)
        )
    }, [allProductGroups, selectedProducts])

    // Color palette for product lines
    const productColors = SCHEMES.default

    // Data for monthly trend chart
    const monthlyChartData = useMemo(() => {
        if (!revenueData.length) return []

        // Group by month
        const monthlyData = revenueData.reduce(
            (acc, item) => {
                const month = item.revenue_month
                if (!acc[month]) {
                    acc[month] = {
                        month,
                        revenue: 0,
                        cost: 0,
                    }
                }
                acc[month].revenue +=
                    item.revenue_amount || item.allocated_amount || 0
                return acc
            },
            {} as Record<
                string,
                { month: string; revenue: number; cost: number }
            >
        )

        // Add cost data (support both cost_month and revenue_month)
        costData.forEach((item) => {
            const month = item.cost_month || item.revenue_month
            if (month && monthlyData[month]) {
                monthlyData[month].cost += item.cost_amount || 0
            }
        })

        // Convert to array and calculate gross profit
        return Object.values(monthlyData)
            .map((item) => ({
                ...item,
                grossProfit: item.revenue - item.cost,
            }))
            .sort((a, b) => a.month.localeCompare(b.month))
    }, [revenueData, costData])

    return (
        <Card className="space-y-2 p-4">
            {/* Toggle between views */}
            <ButtonGroupCustomized
                className="p-2"
                active={chartView}
                onChange={(value) =>
                    setChartView(value as 'product' | 'monthly')
                }
                buttons={[
                    {
                        label: t('by_product_group'),
                        value: 'product',
                    },
                    {
                        label: t('by_month'),
                        value: 'monthly',
                    },
                ]}
            />
            <div className="flex items-center justify-end">
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowFilters(!showFilters)}
                    endIcon={
                        showFilters ? (
                            <DownIcons />
                        ) : (
                            <DownIcons className="rotate-180" />
                        )
                    }
                >
                    {t('filter_products')} ({selectedProducts?.size}/
                    {allProductGroups.length})
                </Button>
            </div>
            {showFilters && (
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 transition-all duration-300 ease-in-out">
                    <div className="flex items-center justify-between mb-3">
                        <span className="font-medium text-gray-700 text-sm">
                            {t('select_products_to_display')}
                        </span>
                        <Button
                            size="small"
                            onClick={handleToggleAll}
                            variant="text"
                            className="text-sm"
                        >
                            {selectedProducts.size === allProductGroups.length
                                ? t('deselect_all')
                                : t('select_all')}
                        </Button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {allProductGroups.map((product, index) => (
                            <div
                                key={index}
                                className="flex items-center space-x-2 line-clamp-1"
                            >
                                <Checkbox
                                    size="small"
                                    checked={selectedProducts?.has(product)}
                                    onChange={() =>
                                        handleToggleProduct(product)
                                    }
                                />
                                <Typogaphy className="text-sm line-clamp-1">
                                    {product}
                                </Typogaphy>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Chart */}
            <ResponsiveContainer width="100%" height={500}>
                {chartView === 'product' ? (
                    <LineChart
                        data={productTrendData}
                        margin={{ top: 20, right: 30, bottom: 20, left: 60 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" tick={{ fontSize: 14 }} />
                        <YAxis
                            tickFormatter={(value) =>
                                `${(value / 1000000).toFixed(0)}M`
                            }
                            tick={{ fontSize: 14 }}
                        />
                        <Tooltip
                            formatter={(value: number) => formatCurrency(value)}
                            contentStyle={{
                                backgroundColor: 'white',
                                border: '1px solid #ccc',
                                fontSize: '14px',
                            }}
                        />
                        <Legend
                            wrapperStyle={{
                                paddingTop: '20px',
                                fontSize: '14px',
                            }}
                        />
                        {visibleProductGroups.map((product) => {
                            const index = allProductGroups.indexOf(product)
                            return (
                                <Line
                                    key={product}
                                    type="monotone"
                                    dataKey={product}
                                    name={product}
                                    stroke={
                                        productColors[
                                            index % productColors.length
                                        ]
                                    }
                                    strokeWidth={1}
                                    dot={{ r: 2 }}
                                    activeDot={{ r: 4 }}
                                />
                            )
                        })}
                    </LineChart>
                ) : (
                    <LineChart
                        data={monthlyChartData}
                        margin={{ top: 20, right: 30, bottom: 20, left: 60 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                        <YAxis
                            tickFormatter={(value) =>
                                `${(value / 1000000).toFixed(0)}M`
                            }
                            tick={{ fontSize: 12 }}
                        />
                        <Tooltip
                            formatter={(value: number) => formatCurrency(value)}
                            contentStyle={{
                                backgroundColor: 'white',
                                border: '1px solid #ccc',
                            }}
                        />
                        <Legend wrapperStyle={{ paddingTop: '20px' }} />
                        <Line
                            type="monotone"
                            dataKey="revenue"
                            name={t('revenue')}
                            stroke={SCHEMES.info[0]}
                            strokeWidth={1}
                            dot={{ r: 2 }}
                            activeDot={{ r: 4 }}
                        />
                        <Line
                            type="monotone"
                            dataKey="cost"
                            name={t('cost_of_goods')}
                            stroke={SCHEMES.warning[0]}
                            strokeWidth={1}
                            dot={{ r: 2 }}
                            activeDot={{ r: 4 }}
                        />
                        <Line
                            type="monotone"
                            dataKey="grossProfit"
                            name={t('gross_profit')}
                            stroke={SCHEMES.success[0]}
                            strokeWidth={1}
                            dot={{ r: 2 }}
                            activeDot={{ r: 4 }}
                        />
                    </LineChart>
                )}
            </ResponsiveContainer>
        </Card>
    )
}

export default RevenueChart
